import {
  createSlice,
  createSelector,
  createAction,
  PayloadAction,
  current,
} from '@reduxjs/toolkit';
import * as R from 'fp-ts/lib/Record';
import * as O from 'fp-ts/lib/Option';

import { RootState } from 'src/redux/store';
import {
  Alert,
  AlertSettings,
  AlertThreshold,
  AlertWithPersonalDetails,
  AllTenantDevicesDisconnectedAlert,
  BaselineAlertThreshold,
  PatientAlertTypeSettings,
  PatientAttentionItem,
} from 'src/types/alerts';
import { PageMetaDataType, UUID } from 'src/types/utility';
import { ALERT_STATUS_ENUM } from 'src/utils/constants';
import {
  DATA_STATE_KEY,
  DATA_FETCHING_STATUS,
  ALERT_METRIC_ENUM,
} from '../../constants';
import {
  AlertsMap,
  AlertSoundsMap,
  PacientIdPayload,
  SoundsToBePlayed,
} from './types';
import {
  mergeArraysWithUniqueIds,
  mergeRecordsWithUniqueIds,
} from '../../dataUtils';
import { mapPatientAlertsByPatientId, partitionAlertsByType } from './utils';
import { API_STATUS } from 'src/utils/api-constants';
import {
  FetchMTMAttentionListResponse,
  getPatientsAlertsToggleSettingsResponse,
} from 'src/services/types';
import {
  ActivityAlertSettings,
  SelectedLevelAlertSettings,
} from 'src/components/AlertSettingsComponents/ActivityAlerts/types';

export const STATE_KEY = 'alerts';

// TODO: Change to enum
type LoadingStatus = 'LOADING' | 'SUCCESS' | 'ERROR' | 'INITIAL';

export const INITIAL_STATE: {
  alerts: Alert[];
  firstAlertReceived: Alert | null;
  firstAttentionListAlertReceived:
    | PatientAttentionItem
    | AllTenantDevicesDisconnectedAlert
    | null;
  alertsMetadata: {
    page: PageMetaDataType;
    sort: unknown;
  } | null;
  attentionListMetadata: {
    page: PageMetaDataType;
    sort: unknown;
  } | null;
  patientCurrentAlerts: AlertsMap;
  mtmAttentionList: FetchMTMAttentionListResponse['data'];
  alertSounds: AlertSoundsMap;
  thresholds: AlertThreshold[];
  baselineThresholds: BaselineAlertThreshold[];
  alertSettings: {
    tenant: Partial<AlertSettings>;
    list: AlertSettings[];
  };
  alertsToggleSettings: { bedExit: boolean; positionChange: boolean };
  isBedExitAlertEnabled: Record<UUID, boolean>;
  isPositionChangeAlertEnabled: Record<UUID, boolean>;
  thresholdStatus: LoadingStatus;
  baseLineThresholdStatus: LoadingStatus;
  alertsStatus: LoadingStatus;
  historicalAlertsStatus: LoadingStatus;
  alertTypesStatus: LoadingStatus;
  alertsToggleStatus: LoadingStatus;
  alertsSettingsStatus: LoadingStatus;
  mtmAttentionListStatus: LoadingStatus;
  ongoingAlertProcesses: {
    clear: Record<UUID, boolean>;
  };
  alertWithPersonalDetails: {
    data: O.Option<AlertWithPersonalDetails>;
    apiStatus: API_STATUS;
  };
  areAlertsMuted: boolean;
  areNewAlertsFromLastSync: boolean;
  areNewAttentionListAlertsFromLastSync: boolean;
  activityAlertSettings: {
    data: ActivityAlertSettings;
    extraProps: {
      isLoading: LoadingStatus;
    };
  };
} = {
  alerts: [],
  firstAlertReceived: null,
  firstAttentionListAlertReceived: null,
  patientCurrentAlerts: {},
  alertsMetadata: {
    page: { totalResults: 0, page: 0, limit: 0 },
    sort: null,
  },
  attentionListMetadata: {
    page: { totalResults: 0, page: 0, limit: 0 },
    sort: null,
  },
  mtmAttentionList: [],
  alertSounds: {},
  thresholds: [],
  baselineThresholds: [],
  alertSettings: {
    tenant: {},
    list: [],
  },
  alertsToggleSettings: { bedExit: false, positionChange: false },
  isBedExitAlertEnabled: {},
  isPositionChangeAlertEnabled: {},
  thresholdStatus: DATA_FETCHING_STATUS.LOADING,
  baseLineThresholdStatus: DATA_FETCHING_STATUS.LOADING,
  alertsStatus: DATA_FETCHING_STATUS.LOADING,
  historicalAlertsStatus: DATA_FETCHING_STATUS.LOADING,
  alertTypesStatus: DATA_FETCHING_STATUS.LOADING,
  alertsToggleStatus: DATA_FETCHING_STATUS.LOADING,
  alertsSettingsStatus: DATA_FETCHING_STATUS.LOADING,
  mtmAttentionListStatus: DATA_FETCHING_STATUS.LOADING,
  ongoingAlertProcesses: {
    clear: {},
  },
  alertWithPersonalDetails: {
    data: O.none,
    apiStatus: API_STATUS.LOADING,
  },
  areAlertsMuted: false,
  areNewAlertsFromLastSync: false,
  areNewAttentionListAlertsFromLastSync: false,
  activityAlertSettings: {
    data: {
      uniqueChildTenantAlertSettings: [
        {
          tenantIds: [],
          outOfBedAlertSettings: {
            shortlyAfterBedExitAlertEnabled: false,
            bedtimeExitAlertSettings: {
              startTime: '00:00',
              endTime: '00:00',
              continuousOutOfBedAlertSettings: {
                thresholdDuration: 0,
                enabled: false,
              },
              repeatingStepOutOfBedAlertSettings: {
                minimumRepetitions: 0,
                minimumDuration: 0,
                maximumDuration: 0,
                enabled: false,
              },
            },
          },
          inBedAlertSettings: {
            percentChange: 0,
            percentChangeInBedExits: 0,
            durationBaselineDays: 0,
            deviationPeriodDays: 0,
            enabled: false,
          },
        },
      ],
      selectedLevelAlertSettings: {
        tenantIds: [],
        outOfBedAlertSettings: {
          shortlyAfterBedExitAlertEnabled: false,
          bedtimeExitAlertSettings: {
            startTime: '00:00',
            endTime: '00:00',
            continuousOutOfBedAlertSettings: {
              thresholdDuration: 0,
              enabled: false,
            },
            repeatingStepOutOfBedAlertSettings: {
              minimumRepetitions: 0,
              minimumDuration: 0,
              maximumDuration: 0,
              enabled: false,
            },
          },
        },
        inBedAlertSettings: {
          percentChange: 0,
          percentChangeInBedExits: 0,
          durationBaselineDays: 0,
          deviationPeriodDays: 0,
          enabled: false,
        },
      },
      uniqueChildTenantIds: [],
    },
    extraProps: {
      isLoading: API_STATUS.INITIAL,
    },
  },
};

const slice = createSlice({
  name: STATE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    fetchAlertsSuccess: (
      state,
      action: PayloadAction<{
        alertsList: Alert[];
        alertsMap: AlertsMap;
        metadata?: {
          page: PageMetaDataType;
          sort: unknown;
        } | null;
        shouldResetData?: boolean;
        shouldUpdateMetadata?: boolean;
      }>,
    ) => {
      const {
        alertsList,
        alertsMap,
        metadata,
        shouldResetData = false,
        shouldUpdateMetadata = false,
      } = action.payload;
      if (shouldResetData) {
        state.alerts = alertsList;
        state.patientCurrentAlerts = alertsMap;
      } else {
        state.alerts = mergeArraysWithUniqueIds(state.alerts, alertsList).sort(
          (a, b) => b.startTime.localeCompare(a.startTime),
        );

        state.patientCurrentAlerts = mergeRecordsWithUniqueIds(
          state.patientCurrentAlerts,
          alertsMap,
        );
      }
      if (metadata && shouldUpdateMetadata) {
        state.alertsMetadata = metadata;
      }
      state.alertsStatus = DATA_FETCHING_STATUS.SUCCESS;
    },
    updateAlertsSuccess: (
      state,
      action: PayloadAction<{
        alertsList: Alert[];
      }>,
    ) => {
      const { alertsList } = action.payload;

      state.alerts = mergeArraysWithUniqueIds(state.alerts, alertsList).sort(
        (a, b) => b.startTime.localeCompare(a.startTime),
      );
    },
    setFirstAlertsSuccess: (state, action: PayloadAction<Alert | null>) => {
      state.firstAlertReceived = action.payload;
    },
    setFirstAttentionListAlertReceived: (
      state,
      action: PayloadAction<
        PatientAttentionItem | AllTenantDevicesDisconnectedAlert | null
      >,
    ) => {
      state.firstAttentionListAlertReceived = action.payload;
    },
    setAlertsStatus: (
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) => {
      state.alertsStatus = action.payload;
    },
    setHistoricalAlertsStatus: (
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) => {
      state.historicalAlertsStatus = action.payload;
    },
    fetchHistoricalAlertsSuccess: (state, action: PayloadAction<Alert[]>) => {
      const alertsList = action.payload;

      state.alerts = mergeArraysWithUniqueIds(state.alerts, alertsList).sort(
        (a, b) => b.startTime.localeCompare(a.startTime),
      );
      state.historicalAlertsStatus = DATA_FETCHING_STATUS.SUCCESS;
    },
    fetchAlertsFailed: state => {
      state.alertsStatus = DATA_FETCHING_STATUS.ERROR;
    },
    fetchMTMAttentionListSuccess: (
      state,
      action: PayloadAction<{
        mtmAttentionList: FetchMTMAttentionListResponse['data'];
      }>,
    ) => {
      state.mtmAttentionList = action.payload.mtmAttentionList;
    },
    fetchMTMAttentionListMetadataSuccess: (
      state,
      action: PayloadAction<FetchMTMAttentionListResponse['metadata']>,
    ) => {
      state.attentionListMetadata = action.payload;
    },
    setMTMAttentionListStatus(
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) {
      state.mtmAttentionListStatus = action.payload;
    },
    suppressAlertTypeSuccess: (state, action: PayloadAction<Alert>) => {
      const newAlert = action.payload;

      state.alerts = mergeArraysWithUniqueIds(state.alerts, [newAlert]).sort(
        (a, b) => b.startTime.localeCompare(a.startTime),
      );

      const newPatientAlerts = partitionAlertsByType(
        mapPatientAlertsByPatientId([
          { patientId: newAlert.patientId, alerts: [newAlert] },
        ]),
      );

      state.patientCurrentAlerts = mergeRecordsWithUniqueIds(
        state.patientCurrentAlerts,
        newPatientAlerts,
      );
    },
    unSuppressAlertTypeSuccess: (state, action: PayloadAction<Alert>) => {
      const newAlert = action.payload;

      state.alerts = mergeArraysWithUniqueIds(state.alerts, [newAlert]).sort(
        (a, b) => b.startTime.localeCompare(a.startTime),
      );

      const newPatientAlerts = partitionAlertsByType(
        mapPatientAlertsByPatientId([
          { patientId: newAlert.patientId, alerts: [newAlert] },
        ]),
      );

      state.patientCurrentAlerts = mergeRecordsWithUniqueIds(
        state.patientCurrentAlerts,
        newPatientAlerts,
      );
    },
    clearAllPatientAlertsSuccess: (state, action: PayloadAction<Alert>) => {
      const newAlert = action.payload;
      const clearedPatientId = newAlert.patientId;
      const clearedAlerts: Alert[] = current(state.alerts)
        .filter(a => a.patientId === clearedPatientId && a.status !== 'OFF')
        .map(a => ({
          ...a,
          status: 'OFF',
          endTime: newAlert.endTime,
        }));

      state.alerts = mergeArraysWithUniqueIds(state.alerts, clearedAlerts).sort(
        (a, b) => b.startTime.localeCompare(a.startTime),
      );

      const newPatientAlerts = partitionAlertsByType(
        mapPatientAlertsByPatientId([
          { patientId: clearedPatientId, alerts: clearedAlerts },
        ]),
      );

      state.patientCurrentAlerts = mergeRecordsWithUniqueIds(
        state.patientCurrentAlerts,
        newPatientAlerts,
      );
    },
    gotAlertsFromAws: (
      state,
      action: PayloadAction<{
        alertsList: Alert[];
        alertsMap: AlertsMap;
      }>,
    ) => {
      const { alertsList, alertsMap } = action.payload;

      state.alerts = mergeArraysWithUniqueIds(state.alerts, alertsList).sort(
        (a, b) => b.startTime.localeCompare(a.startTime),
      );

      state.patientCurrentAlerts = mergeRecordsWithUniqueIds(
        state.patientCurrentAlerts,
        alertsMap,
      );
    },
    alertSoundsChanged: (state, action: PayloadAction<AlertSoundsMap>) => {
      state.alertSounds = mergeRecordsWithUniqueIds(
        state.alertSounds,
        action.payload,
      );
    },
    alertSoundsPlayed: (state, action: PayloadAction<AlertSoundsMap>) => {
      state.alertSounds = mergeRecordsWithUniqueIds(
        state.alertSounds,
        action.payload,
      );
    },
    setThresholdsStatus: (state, action: PayloadAction<LoadingStatus>) => {
      state.thresholdStatus = action.payload;
    },
    setBaselineThresholdsStatus: (
      state,
      action: PayloadAction<LoadingStatus>,
    ) => {
      state.baseLineThresholdStatus = action.payload;
    },
    getThresholdsListSuccess: (
      state,
      action: PayloadAction<AlertThreshold[]>,
    ) => {
      state.thresholds = mergeArraysWithUniqueIds(
        state.thresholds,
        action.payload,
      );
      state.thresholdStatus = DATA_FETCHING_STATUS.SUCCESS;
    },
    getThresholdsListFailed: state => {
      state.thresholdStatus = DATA_FETCHING_STATUS.ERROR;
    },
    createAlertThresholdSuccess: (
      state,
      action: PayloadAction<AlertThreshold>,
    ) => {
      state.thresholds = mergeArraysWithUniqueIds(state.thresholds, [
        action.payload,
      ]);
    },
    editAlertThresholdSuccess: (
      state,
      action: PayloadAction<AlertThreshold>,
    ) => {
      state.thresholds = mergeArraysWithUniqueIds(state.thresholds, [
        action.payload,
      ]);
    },
    getBaselineThresholdsListSuccess: (
      state,
      action: PayloadAction<BaselineAlertThreshold[]>,
    ) => {
      state.baselineThresholds = mergeArraysWithUniqueIds(
        state.baselineThresholds,
        action.payload,
      );
      state.baseLineThresholdStatus = DATA_FETCHING_STATUS.SUCCESS;
    },
    getBaselineThresholdsListFailed: state => {
      state.baseLineThresholdStatus = DATA_FETCHING_STATUS.ERROR;
    },
    createBaselineAlertThresholdSuccess: (
      state,
      action: PayloadAction<BaselineAlertThreshold>,
    ) => {
      state.baselineThresholds = mergeArraysWithUniqueIds(
        state.baselineThresholds,
        [action.payload],
      );
    },
    editBaselineAlertThresholdSuccess: (
      state,
      action: PayloadAction<BaselineAlertThreshold>,
    ) => {
      state.baselineThresholds = mergeArraysWithUniqueIds(
        state.baselineThresholds,
        [action.payload],
      );
    },
    getAlertSettingsSuccess: (state, action: PayloadAction<AlertSettings>) => {
      state.alertSettings.tenant = action.payload;
      state.alertsSettingsStatus = DATA_FETCHING_STATUS.SUCCESS;
    },
    updateAlertSettingsSuccess: (
      state,
      action: PayloadAction<AlertSettings>,
    ) => {
      state.alertSettings.tenant = action.payload;
      state.alertsSettingsStatus = DATA_FETCHING_STATUS.SUCCESS;
    },
    getAlertSettingsListSuccess: (
      state,
      action: PayloadAction<AlertSettings[]>,
    ) => {
      state.alertSettings.list = mergeArraysWithUniqueIds(
        state.alertSettings.list,
        action.payload,
      );
      state.alertsSettingsStatus = DATA_FETCHING_STATUS.SUCCESS;
    },
    updateAlertSettingsListSuccess: (
      state,
      action: PayloadAction<AlertSettings[]>,
    ) => {
      state.alertSettings.list = mergeArraysWithUniqueIds(
        state.alertSettings.list,
        action.payload,
      );
      state.alertsSettingsStatus = DATA_FETCHING_STATUS.SUCCESS;
    },
    getAlertsSettingsFailed: state => {
      state.alertsSettingsStatus = DATA_FETCHING_STATUS.ERROR;
    },
    onClearPatientAlertFinished: (
      state,
      action: PayloadAction<PacientIdPayload>,
    ) => {
      state.ongoingAlertProcesses.clear = {
        [action.payload]: false,
      };
    },
    onPatientMonitoringStopped: (state, action: PayloadAction<UUID>) => {
      const patientId = action.payload;
      delete state.patientCurrentAlerts[patientId];

      state.alerts = state.alerts.filter(
        alert => !(alert.patientId === patientId && alert.status !== 'OFF'),
      );
    },
    getPatientsBedActivityAlertStatusSuccess: (
      state,
      action: PayloadAction<Record<UUID, boolean>>,
    ) => {
      state.isBedExitAlertEnabled = action.payload;
      state.alertTypesStatus = DATA_FETCHING_STATUS.SUCCESS;
    },
    getPatientsAlertsToggleSettingsSuccess: (
      state,
      action: PayloadAction<
        getPatientsAlertsToggleSettingsResponse['canToggleByPatient']
      >,
    ) => {
      state.alertsToggleSettings = action.payload;
      state.alertsToggleStatus = DATA_FETCHING_STATUS.SUCCESS;
    },
    getPatientsPositionChangeAlertStatusSuccess: (
      state,
      action: PayloadAction<Record<UUID, boolean>>,
    ) => {
      state.isPositionChangeAlertEnabled = action.payload;
      state.alertTypesStatus = DATA_FETCHING_STATUS.SUCCESS;
    },
    getPatientsBedActivityAlertStatusFailed: state => {
      state.alertTypesStatus = DATA_FETCHING_STATUS.ERROR;
    },
    getPatientsAlertsToggleSettingsFailed: state => {
      state.alertsToggleStatus = DATA_FETCHING_STATUS.ERROR;
    },
    setPatientBedActivityAlertStatusSuccess(
      state,
      action: PayloadAction<PatientAlertTypeSettings>,
    ) {
      const { patientId, bedExitEnabled, positionChangeEnabled } =
        action.payload;
      state.isBedExitAlertEnabled[patientId] = bedExitEnabled || false;
      state.isPositionChangeAlertEnabled[patientId] =
        positionChangeEnabled || false;
    },
    setPatientBedActivityAlertStatusFailed() {
      // TODO: Add set alert type status if needed
    },
    fetchAlertWithPersonalDetailsSuccess(
      state,
      action: PayloadAction<AlertWithPersonalDetails>,
    ) {
      state.alertWithPersonalDetails = {
        data: O.some(action.payload),
        apiStatus: API_STATUS.OK,
      };
    },
    fetchAlertWithPersonalDetailsFailed(state) {
      state.alertWithPersonalDetails = {
        data: O.none,
        apiStatus: API_STATUS.ERROR,
      };
    },
    toggleMuteAlert(state) {
      state.areAlertsMuted = !state.areAlertsMuted;
    },
    setAreNewAlersFromLastSync(
      state,
      action: PayloadAction<{
        areNewAlerts: boolean;
      }>,
    ) {
      state.areNewAlertsFromLastSync = action.payload.areNewAlerts;
    },
    setAreNewAttentionListAlertsFromLastSync(
      state,
      action: PayloadAction<{
        areNewAlerts: boolean;
      }>,
    ) {
      state.areNewAttentionListAlertsFromLastSync = action.payload.areNewAlerts;
    },
    fetchActivityAlertSettingsSuccess(
      state,
      action: PayloadAction<ActivityAlertSettings>,
    ) {
      state.activityAlertSettings.data = action.payload;
      state.activityAlertSettings.extraProps.isLoading =
        DATA_FETCHING_STATUS.SUCCESS;
    },
    fetchActivityAlertSettingsFailed(state) {
      state.activityAlertSettings.extraProps.isLoading =
        DATA_FETCHING_STATUS.ERROR;
    },
  },
  extraReducers: {},
});

const getState = (state: RootState) =>
  state[DATA_STATE_KEY][STATE_KEY] || INITIAL_STATE;
const getPatientId = (_state: RootState, patientId: UUID) => patientId;
const getSingleAlertId = (_state: RootState, alertId: UUID) => alertId;
const getAlertId = (_state: RootState, _patientId: UUID, alertId: UUID) =>
  alertId;

export const selectors = {
  selectThresholds: createSelector(getState, state => state.thresholds),
  selectBaselineThresholds: createSelector(
    getState,
    state => state.baselineThresholds,
  ),
  getThresholdsStatus: createSelector(getState, state => state.thresholdStatus),
  areThresholdsLoading: createSelector(
    getState,
    state => state.thresholdStatus === DATA_FETCHING_STATUS.LOADING,
  ),
  getBaselineThresholdsStatus: createSelector(
    getState,
    state => state.baseLineThresholdStatus,
  ),
  getActivityAlertSettingsStatus: createSelector(
    getState,
    state => state.activityAlertSettings.extraProps.isLoading,
  ),
  areBaselineThresholdsLoading: createSelector(
    getState,
    state => state.baseLineThresholdStatus === DATA_FETCHING_STATUS.LOADING,
  ),
  getAlertsStatus: createSelector(getState, state => state.alertsStatus),
  areAlertsLoading: createSelector(
    getState,
    state => state.alertsStatus === DATA_FETCHING_STATUS.LOADING,
  ),
  areHistoricalAlertsLoading: createSelector(
    getState,
    state => state.historicalAlertsStatus === DATA_FETCHING_STATUS.LOADING,
  ),
  getMtmAttentionListStatus: createSelector(
    getState,
    state => state.mtmAttentionListStatus,
  ),
  selectAlertTypesStatus: createSelector(
    getState,
    state => state.alertTypesStatus,
  ),
  selectAlerts: createSelector(getState, state => state.alerts),
  selectLastDayActiveAlerts: createSelector(getState, state =>
    state.alerts.filter(
      alert =>
        alert.status !== ALERT_STATUS_ENUM.OFF &&
        // DEVICE_DISCONNECTED alerts are not working needs BE adjustment therfor are filtered out
        alert.thresholdMetric !== ALERT_METRIC_ENUM.DEVICE_DISCONNECTED,
    ),
  ),
  selectAlertsMetadata: createSelector(getState, state => state.alertsMetadata),
  selectAlertById: createSelector(
    getState,
    getSingleAlertId,
    (state, alertId) => state.alerts.find(a => a.id === alertId),
  ),
  selectPatientAlerts: createSelector(
    getState,
    state => state.patientCurrentAlerts,
  ),
  selectEnabledBedExitAlerts: createSelector(
    getState,
    state => state.isBedExitAlertEnabled,
  ),
  selectIsBedExitAlertActive: createSelector(
    getState,
    getPatientId,
    (state, patientId) =>
      state.patientCurrentAlerts[patientId]?.BED_EXIT?.status ===
        ALERT_STATUS_ENUM.ON || false,
  ),
  selectIsBedExitAlertEnabled: createSelector(
    getState,
    getPatientId,
    (state, patientId) => state.isBedExitAlertEnabled[patientId] || false,
  ),
  selectIsPositionChangeAlertEnabled: createSelector(
    getState,
    getPatientId,
    (state, patientId) =>
      state.isPositionChangeAlertEnabled[patientId] || false,
  ),
  selectLongOutOfBedAlertActive: createSelector(
    getState,
    getPatientId,
    (state, patientId) =>
      state.patientCurrentAlerts[patientId]?.LONG_OUT_OF_BED?.status ===
        ALERT_STATUS_ENUM.ON || false,
  ),
  selectBedExitAlert: createSelector(
    getState,
    getPatientId,
    (state, patientId) => state.patientCurrentAlerts[patientId]?.BED_EXIT,
  ),
  getAlertsByPatientId: createSelector(
    getState,
    getPatientId,
    (state, patientId) => state.patientCurrentAlerts[patientId],
  ),
  selectOngoingAlertsByPatientId: createSelector(
    getState,
    getPatientId,
    (state, patientId) => {
      return R.filter<Alert>(alert => alert && alert.status !== 'OFF')(
        state.patientCurrentAlerts[patientId] || {},
      );
    },
  ),
  selectPatientAlert: createSelector(
    getState,
    getPatientId,
    getAlertId,
    (state, patientId, alertId) =>
      Object.values(state.patientCurrentAlerts[patientId] || {}).find(
        a => a.id === alertId,
      ),
  ),
  getAlertsSettings: createSelector(
    getState,
    state => state.alertSettings.tenant,
  ),
  getAlertsSettingsList: createSelector(
    getState,
    state => state.alertSettings.list,
  ),
  selectAlertSounds: createSelector(getState, state => state.alertSounds),
  selectOngoingClearAlertsByPacientId: createSelector(
    getState,
    getPatientId,
    (state, patientId) => state.ongoingAlertProcesses.clear[patientId] || false,
  ),
  selectAlertWithPersonalDetails: createSelector(
    getState,
    state => state.alertWithPersonalDetails,
  ),
  selectMTMAttentionList: createSelector(
    getState,
    state => state.mtmAttentionList,
  ),
  getAreAlertsMuted: createSelector(getState, state => state.areAlertsMuted),
  getAttentionListMetadata: createSelector(
    getState,
    state => state.attentionListMetadata,
  ),
  getAreNewAlertsFromLastSync: createSelector(
    getState,
    state => state.areNewAlertsFromLastSync,
  ),
  getAreNewAttentionListAlertsFromLastSync: createSelector(
    getState,
    state => state.areNewAttentionListAlertsFromLastSync,
  ),
  selectMTMAlertDeviceIds: createSelector(getState, state =>
    state.alerts.map(elem => elem.deviceId),
  ),
  selectActivityAlertSettings: createSelector(
    getState,
    state => state.activityAlertSettings.data,
  ),
  selectFirstAlertReceived: createSelector(
    getState,
    state => state.firstAlertReceived,
  ),
  selectFirstAttentionListAlertReceived: createSelector(
    getState,
    state => state.firstAttentionListAlertReceived,
  ),
  selectAlertsToggleSettings: createSelector(
    getState,
    state => state.alertsToggleSettings,
  ),
};

const extraActions = {
  getThresholdsList: createAction(`${STATE_KEY}/getThresholdsList`),
  createAlert: createAction<
    Pick<AlertThreshold, 'enable' | 'metric' | 'preposition' | 'value'>
  >(`${STATE_KEY}/createAlert`),
  editAlert: createAction<Pick<AlertThreshold, 'id' | 'value' | 'enable'>>(
    `${STATE_KEY}/editAlert`,
  ),
  getBaselineThresholdsList: createAction(
    `${STATE_KEY}/getBaselineThresholdsList`,
  ),
  createBaselineAlertThreshold: createAction<
    Pick<
      BaselineAlertThreshold,
      | 'enable'
      | 'metric'
      | 'baselineDaysInterval'
      | 'deviationHoursInterval'
      | 'deviationPercentage'
    >
  >(`${STATE_KEY}/createBaselineThreshold`),
  updateBaselineAlertThreshold: createAction<
    { id: UUID } & Partial<
      Pick<
        BaselineAlertThreshold,
        | 'enable'
        | 'baselineDaysInterval'
        | 'deviationHoursInterval'
        | 'deviationPercentage'
      >
    >
  >(`${STATE_KEY}/updateBaselineThreshold`),
  deleteAlert: createAction<UUID>(`${STATE_KEY}/deleteAlert`),
  getPatientsBedActivityAlertStatus: createAction<UUID[]>(
    `${STATE_KEY}/getPatientsBedActivityAlertStatus`,
  ),
  getPatientsAlertsToggleSettings: createAction(
    `${STATE_KEY}/getPatientsAlertsToggleSettings`,
  ),
  setPatientBedActivityAlertStatus: createAction<{
    patientId: UUID;
    isBedExitAlertEnabled: boolean;
    isPositionChangeEnabled: boolean;
  }>(`${STATE_KEY}/setPatientBedActivityAlertStatus`),
  fetchPatientAlerts: createAction(`${STATE_KEY}/fetchPatientAlerts`),
  fetchHistoricalAlerts: createAction<UUID[]>(
    `${STATE_KEY}/fetchHistoricalAlerts`,
  ),
  playSoundForAlerts: createAction<SoundsToBePlayed>(
    `${STATE_KEY}/playSoundForAlerts`,
  ),
  suppressAlertType: createAction<UUID>(`${STATE_KEY}/suppressAlertType`),
  unSuppressAlertType: createAction<UUID>(`${STATE_KEY}/unSuppressAlertType`),
  clearAllPatientAlerts: createAction<UUID>(
    `${STATE_KEY}/clearAllPatientAlerts`,
  ),
  fetchAlertSettings: createAction(`${STATE_KEY}/fetchAlertSettings`),
  fetchAlertWithPersonalDetails: createAction<UUID>(
    `${STATE_KEY}/fetchAlertWithPersonalDetails`,
  ),
  fetchActivityAlertSettings: createAction(
    `${STATE_KEY}/fetchActivityAlertSettings`,
  ),
  fetchMTMActivityAlertSettings: createAction(
    `${STATE_KEY}/fetchMTMActivityAlertSettings`,
  ),
  fetchMTMAttentionList: createAction<
    | {
        pagination?: {
          page: number;
          limit: number;
        };
        shouldResetData?: boolean;
        shouldUpdateMetadata?: boolean;
        isRefreshFunctionFired?: boolean;
        searchQuery?: string;
      }
    | undefined
  >(`${STATE_KEY}/fetchMTMAttentionList`),
  updateActivityAlertSettings: createAction<SelectedLevelAlertSettings>(
    `${STATE_KEY}/updateActivityAlertSettings`,
  ),
  updateMTMActivityAlertSettings: createAction<SelectedLevelAlertSettings>(
    `${STATE_KEY}/updateMTMActivityAlertSettings`,
  ),
};

export const actions = { ...slice.actions, ...extraActions };

const { reducer } = slice;
export default reducer;
